"use client";

import React, { useMemo } from "react";
import Image from "next/image";
import { dataHelper } from "../../data/dataHelper";
import Tooltip from "../tooltip/Tooltip";
import VisaIndicator from "../visa/VisaIndicator";
import { NewIcon } from "../ui/icons/NewIcon/NewIcon";
import ArrowUpIcon from "../ui/icons/ArrowUpIcon/ArrowUpIcon";
import ArrowDownIcon from "../ui/icons/ArrowDownIcon/ArrowDownIcon";
import ArrowUpDownIcon from "../ui/icons/ArrowUpDownIcon/ArrowUpDownIcon";
import MatchScoreRing from "../ats/MatchScoreRing";
import BoostButton from "../ats/BoostButton";
import { formatJobsCompanyLabel, jobsCompanyLogoStem } from "../../app/lib/resume/jobsCompanyMap";

const DEFAULT_THEME = {
  containerBgClass: "bg-white",
  containerBorderClass: "border-gray-200",
  headerBg: "#fbfcff",
  rowBaseClass: "",
  rowHoverClass: "hover:bg-blue-50/30",
  cellBgClass: "",
  stickyCellBgClass: "bg-white",
  logoBgClass: "bg-white",
  applyBtnBorderClass: "border-gray-300",
  applyBtnTextColor: "#596379",
  applyBtnHoverTextClass: "hover:text-blue-900",
  skeletonBarClass: "bg-slate-200",
};

const SKELETON_COMPANY_WIDTHS = ["w-20", "w-24", "w-16", "w-28", "w-24"];
const SKELETON_POSITION_WIDTHS = ["w-44", "w-36", "w-52", "w-40", "w-48", "w-32", "w-56", "w-40"];
const SKELETON_LOCATION_WIDTHS = ["w-24", "w-20", "w-28", "w-16", "w-32"];

function sortJobsByMatch(jobs, matchByJobId, sortDir, rankBy = "ats_percent") {
  const sorted = [...jobs];
  sorted.sort((a, b) => {
    const ma = matchByJobId[String(a.id)];
    const mb = matchByJobId[String(b.id)];
    const sa = ma?.currentPercent;
    const sb = mb?.currentPercent;
    const aMissing = sa == null || !Number.isFinite(sa);
    const bMissing = sb == null || !Number.isFinite(sb);
    if (aMissing && bMissing) return 0;
    if (aMissing) return 1;
    if (bMissing) return -1;
    const asc = sortDir === "asc";
    const dir = asc ? 1 : -1;
    if (rankBy === "match_count") {
      const ca = Number.isFinite(ma?.matchedCount) ? ma.matchedCount : (ma?.matched?.length ?? 0);
      const cb = Number.isFinite(mb?.matchedCount) ? mb.matchedCount : (mb?.matched?.length ?? 0);
      if (ca !== cb) return dir * (ca - cb);
      const ha = Number.isFinite(ma?.matchedHardCount) ? ma.matchedHardCount : 0;
      const hb = Number.isFinite(mb?.matchedHardCount) ? mb.matchedHardCount : 0;
      if (ha !== hb) return dir * (ha - hb);
      const wa = Number.isFinite(ma?.matchedWeight) ? ma.matchedWeight : 0;
      const wb = Number.isFinite(mb?.matchedWeight) ? mb.matchedWeight : 0;
      if (wa !== wb) return dir * (wa - wb);
      if (sa !== sb) return dir * (sa - sb);
      return 0;
    }
    return dir * (sa - sb);
  });
  return sorted;
}

export default function JobsTable({ data, isLoading, isError, sortBy, sortDir, onSortChange, theme = {}, skeletonRows = 8 }) {
  const mergedTheme = { ...DEFAULT_THEME, ...theme };
  const showSaveColumn = Boolean(theme?.showSaveColumn);
  const showMatchColumn = Boolean(theme?.showMatchColumn);
  const showBoostColumn = showMatchColumn && theme?.showBoostColumn !== false;
  const matchDebug = Boolean(theme?.matchDebug);
  const savedJobIds = new Set((Array.isArray(theme?.savedJobIds) ? theme.savedJobIds : []).map((id) => String(id)));
  const onToggleSave = typeof theme?.onToggleSave === "function" ? theme.onToggleSave : null;
  const saveBusyIds = new Set((Array.isArray(theme?.saveBusyIds) ? theme.saveBusyIds : []).map((id) => String(id)));
  const matchByJobId = theme?.matchByJobId && typeof theme.matchByJobId === "object" ? theme.matchByJobId : {};
  const onBoost = typeof theme?.onBoost === "function" ? theme.onBoost : null;
  const onMatchClick = typeof theme?.onMatchClick === "function" ? theme.onMatchClick : null;
  const matchLoading = Boolean(theme?.matchLoading);
  const hasResumeKeywords = Boolean(theme?.hasResumeKeywords);
  const columnsCount = 6 + (showMatchColumn ? 1 : 0) + (showBoostColumn ? 1 : 0);
  const rowCount = Math.max(1, Number(skeletonRows) || 8);

  const rows = useMemo(() => {
    const list = Array.isArray(data) ? data : [];
    // Server already returns global order via /api/jobs/match-sort.
    // Only re-sort client-side when scores were computed for the current page.
    if (!showMatchColumn) return list;
    if (theme?.matchSortServer) return list;
    if (sortBy === "ats_match") {
      return sortJobsByMatch(list, matchByJobId, sortDir === "asc" ? "asc" : "desc", "ats_percent");
    }
    if (sortBy === "match_count") {
      return sortJobsByMatch(list, matchByJobId, sortDir === "asc" ? "asc" : "desc", "match_count");
    }
    return list;
  }, [data, showMatchColumn, sortBy, sortDir, matchByJobId, theme?.matchSortServer]);

  if (isError && !isLoading) return <div className="py-10 text-center text-red-500">Failed to load data.</div>;

  const getSortArrow = (column) => {
    if (sortBy !== column) return <ArrowUpDownIcon />;
    return sortDir === "asc" ? <ArrowUpIcon /> : <ArrowDownIcon />;
  };

  const handleSort = (column) => {
    if (!onSortChange) return;
    if (sortBy === column) {
      onSortChange(column, sortDir === "asc" ? "desc" : "asc");
      return;
    }
    // Match / salary: first click = highest first.
    onSortChange(column, column === "ats_match" || column === "salary_value" ? "desc" : "asc");
  };

  function isNewJob(parsedAt) {
    if (!parsedAt) return false;
    const parsed = new Date(parsedAt);
    const now = new Date();
    return (now - parsed) / (1000 * 60 * 60 * 24) <= 7;
  }

  return (
    <table className="w-full text-left text-xs min-w-[1100px]" aria-busy={isLoading ? "true" : "false"}>
      {/* HEADER */}
      <thead className="top-0 z-20  border-b border-slate-200" style={{ backgroundColor: mergedTheme.headerBg }}>
        <tr className="uppercase text-[10px] tracking-wider text-slate-500">
          <th className="!px-5 !py-3">
            <HeaderContent label="Company" column="company" {...{ sortBy, handleSort, getSortArrow }} />
          </th>
          <Th label="Salary" column="salary_value" {...{ sortBy, handleSort, getSortArrow }} />

          <Th label="Location" column="location" {...{ sortBy, handleSort, getSortArrow }} />
          <Th label="Position" column="position" {...{ sortBy, handleSort, getSortArrow }} />
          {showMatchColumn ? (
            <th
              onClick={() => handleSort("ats_match")}
              className="px-3 py-2 cursor-pointer text-center select-none"
              title="ATS coverage %. Board order is controlled by the Sort select (Best matches / ATS %)."
            >
              <div className="flex items-center justify-center gap-1">
                ATS Match
                <span className="text-[10px] opacity-60">{getSortArrow("ats_match")}</span>
              </div>
            </th>
          ) : null}
          {showBoostColumn ? <th className="px-3 py-2 text-center">Boost</th> : null}
          {showSaveColumn ? <th className="px-3 py-2 text-center">Save</th> : <Th label="Type" column="program_type" {...{ sortBy, handleSort, getSortArrow }} />}
          <th className="px-4 py-3 text-center">Link</th>
        </tr>
      </thead>

      {/* BODY */}
      <tbody className="divide-y divide-slate-100 !text-[#596379]">
        {isLoading ? (
          <JobsTableSkeletonRows
            rowCount={rowCount}
            showMatchColumn={showMatchColumn}
            showBoostColumn={showBoostColumn}
            showSaveColumn={showSaveColumn}
            mergedTheme={mergedTheme}
          />
        ) : rows.length === 0 ? (
          <tr>
            <td colSpan={columnsCount} className={`py-12 text-center text-slate-300 ${mergedTheme.cellBgClass}`}>
              No results
            </td>
          </tr>
        ) : (
          rows.map((job) => {
            const companyLabel = formatJobsCompanyLabel(job.company);
            const logoStem = jobsCompanyLogoStem(job.company);
            return (
            <tr key={job.id} className={`${mergedTheme.rowBaseClass} ${mergedTheme.rowHoverClass} transition-colors`}>
              {/* COMPANY */}
              <td className={`px-3 py-2 font-medium whitespace-nowrap align-middle ${mergedTheme.cellBgClass}`}>
                <div className="flex items-center gap-3">
                  <div className={`logo glass2 shrink-0 ${mergedTheme.logoBgClass}`}>
                    <Image src={`/images/logos/${logoStem}.avif`} alt={companyLabel} width={35} height={35} unoptimized />
                  </div>
                  {companyLabel}
                </div>
              </td>

              {/* SALARY */}
              <td className={`px-3 py-2 align-middle whitespace-nowrap ${mergedTheme.cellBgClass}`}>
                <div className="flex items-center gap-2">
                  {job.salary_value != null ? (
                    <Tooltip lines={[job.salary ? `Official: ${job.salary}` : `Est. ${job.estimated_salary?.toLocaleString()}`]}>
                      <span
                        className={`
            cursor-help px-2 py-1 rounded-md text-xs font-medium
            border
            ${job.salary ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-slate-100 text-slate-500 border-slate-200"}
          `}
                      >
                        ${job.salary_value.toLocaleString()}
                      </span>
                    </Tooltip>
                  ) : (
                    <span className="text-slate-300">-</span>
                  )}
                </div>
              </td>

              {/* LOCATION + VISA */}
              <td className={`px-3 py-2 whitespace-nowrap align-middle ${mergedTheme.cellBgClass}`}>
                <div className="flex items-center gap-2">
                  {job.company && dataHelper.data[job.company] && (
                    <Tooltip lines={dataHelper.tooltip_full[job.company] || []}>
                      <VisaIndicator v={dataHelper.data[job.company].v} />
                    </Tooltip>
                  )}
                  {job.location}
                </div>
              </td>

              {/* POSITION */}
              <td className={`px-3 py-2 whitespace-nowrap align-middle ${mergedTheme.cellBgClass}`}>
                <div className="flex items-center gap-2" title={job.position}>
                  {isNewJob(job.parsed_at) && <NewIcon className="w-5 h-5" />}
                  {job.position.length > 50 ? `${job.position.slice(0, 50)}…` : job.position}
                </div>
              </td>

              {/* ATS MATCH + BOOST */}
              {showMatchColumn ? (
                <td className={`px-3 py-2 whitespace-nowrap align-middle text-center ${mergedTheme.cellBgClass}`}>
                  {(() => {
                    const jobIdKey = String(job.id);
                    const match = matchByJobId[jobIdKey] || null;
                    const emptyHint = !hasResumeKeywords ? "Upload a resume to see ATS Match" : matchLoading ? "Loading ATS Match…" : "ATS Match pending (no job keywords yet)";
                    const ring = (
                      <MatchScoreRing
                        currentPercent={match?.currentPercent ?? null}
                        boostPercent={match?.boostPercent ?? null}
                        band={match?.band || "weak"}
                        emptyHint={emptyHint}
                        debug={matchDebug}
                        matched={match?.matched || []}
                        boostable={match?.boostable || []}
                        missingHard={match?.missingHard || []}
                      />
                    );
                    if (!onMatchClick) {
                      return <div className="flex items-center justify-center">{ring}</div>;
                    }
                    return (
                      <div className="flex items-center justify-center">
                        <button
                          type="button"
                          onClick={() => onMatchClick(job, match)}
                          className="rounded-full p-0.5 hover:ring-2 hover:ring-[#507619]/30 focus:outline-none focus:ring-2 focus:ring-[#507619]/40 cursor-pointer"
                          title="Open ATS keyword analysis"
                          aria-label={`Open ATS analysis for ${job.position || "job"}`}
                        >
                          {ring}
                        </button>
                      </div>
                    );
                  })()}
                </td>
              ) : null}
              {showBoostColumn ? (
                <td className={`px-3 py-2 whitespace-nowrap align-middle text-center ${mergedTheme.cellBgClass}`}>
                  {(() => {
                    const jobIdKey = String(job.id);
                    const match = matchByJobId[jobIdKey] || null;
                    const canBoost = Boolean(match && match.boostPercent > match.currentPercent);
                    return (
                      <div className="flex items-center justify-center">
                        <BoostButton disabled={!canBoost || !onBoost} onClick={() => onBoost?.(job, match)} />
                      </div>
                    );
                  })()}
                </td>
              ) : null}

              {/* TYPE / SAVE */}
              {showSaveColumn ? (
                <td className={`px-3 py-2 whitespace-nowrap align-middle text-center ${mergedTheme.cellBgClass}`}>
                  {(() => {
                    const jobIdKey = String(job.id);
                    const isSaved = savedJobIds.has(jobIdKey);
                    const isBusy = saveBusyIds.has(jobIdKey);
                    return (
                      <button
                        type="button"
                        onClick={() => onToggleSave?.(jobIdKey)}
                        disabled={!onToggleSave || isBusy}
                        className="inline-flex items-center justify-center cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                        aria-label={isSaved ? "Remove from saved jobs" : "Save job"}
                        title={isSaved ? "Remove from favorites" : "Save to favorites"}
                      >
                        {isSaved ? (
                          <svg fill="#fcb60e" viewBox="0 0 1000 1000" className="w-5 h-5" xmlns="http://www.w3.org/2000/svg" stroke="#fcb60e">
                            <path d="M476 801l-181 95q-18 10-36.5 4.5T229 879t-7-36l34-202q2-12-1.5-24T242 596L95 453q-15-14-15.5-33.5T91 385t32-18l203-30q12-2 22-9t16-18l90-184q10-18 28-25t36 0 28 25l90 184q6 11 16 18t22 9l203 30q20 3 32 18t11.5 34.5T905 453L758 596q-8 9-12 21t-2 24l34 202q4 20-7 36t-29.5 21.5T705 896l-181-95q-11-6-24-6t-24 6z"></path>
                          </svg>
                        ) : (
                          <svg viewBox="0 0 1000 1000" className="w-5 h-5 text-[#8b8a86]" xmlns="http://www.w3.org/2000/svg" fill="none" stroke="currentColor">
                            <path
                              d="M476 801l-181 95q-18 10-36.5 4.5T229 879t-7-36l34-202q2-12-1.5-24T242 596L95 453q-15-14-15.5-33.5T91 385t32-18l203-30q12-2 22-9t16-18l90-184q10-18 28-25t36 0 28 25l90 184q6 11 16 18t22 9l203 30q20 3 32 18t11.5 34.5T905 453L758 596q-8 9-12 21t-2 24l34 202q4 20-7 36t-29.5 21.5T705 896l-181-95q-11-6-24-6t-24 6z"
                              strokeWidth="35"
                            ></path>
                          </svg>
                        )}
                      </button>
                    );
                  })()}
                </td>
              ) : (
                <td className={`px-3 py-2 whitespace-nowrap align-middle ${mergedTheme.cellBgClass}`}>{job.program_type}</td>
              )}

              <td className={`px-3 py-2 text-center ${mergedTheme.cellBgClass}`}>
                {job.url ? (
                  <a
                    href={job.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`px-3 py-1 rounded-md border text-xs font-medium text-center ${mergedTheme.applyBtnBorderClass} ${mergedTheme.applyBtnHoverTextClass}`}
                    style={{ color: mergedTheme.applyBtnTextColor }}
                  >
                    Apply
                  </a>
                ) : (
                  <span className="text-slate-400">-</span>
                )}
              </td>
            </tr>
            );
          })
        )}
      </tbody>
    </table>
  );
}

function SkeletonBar({ className = "", barClass }) {
  return <div className={`h-3 rounded ${barClass} animate-pulse ${className}`} />;
}

function JobsTableSkeletonRows({ rowCount, showMatchColumn, showBoostColumn, showSaveColumn, mergedTheme }) {
  const barClass = mergedTheme.skeletonBarClass || DEFAULT_THEME.skeletonBarClass;
  return Array.from({ length: rowCount }, (_, index) => (
    <tr key={`jobs-skeleton-${index}`} className={mergedTheme.rowBaseClass} aria-hidden="true">
      <td className={`px-3 py-2 align-middle ${mergedTheme.cellBgClass}`}>
        <div className="flex items-center gap-3">
          <div className={`logo shrink-0 rounded-[10px] ${barClass} animate-pulse`} />
          <SkeletonBar className={SKELETON_COMPANY_WIDTHS[index % SKELETON_COMPANY_WIDTHS.length]} barClass={barClass} />
        </div>
      </td>
      <td className={`px-3 py-2 align-middle ${mergedTheme.cellBgClass}`}>
        <div className={`h-6 w-16 rounded-md ${barClass} animate-pulse`} />
      </td>
      <td className={`px-3 py-2 align-middle ${mergedTheme.cellBgClass}`}>
        <div className="flex items-center gap-2">
          <div className={`h-4 w-4 rounded-sm ${barClass} animate-pulse`} />
          <SkeletonBar className={SKELETON_LOCATION_WIDTHS[index % SKELETON_LOCATION_WIDTHS.length]} barClass={barClass} />
        </div>
      </td>
      <td className={`px-3 py-2 align-middle ${mergedTheme.cellBgClass}`}>
        <SkeletonBar className={SKELETON_POSITION_WIDTHS[index % SKELETON_POSITION_WIDTHS.length]} barClass={barClass} />
      </td>
      {showMatchColumn ? (
        <td className={`px-3 py-2 align-middle ${mergedTheme.cellBgClass}`}>
          <div className="flex items-center justify-center">
            <div className={`h-11 w-11 rounded-full ${barClass} animate-pulse`} />
          </div>
        </td>
      ) : null}
      {showBoostColumn ? (
        <td className={`px-3 py-2 align-middle ${mergedTheme.cellBgClass}`}>
          <div className="flex items-center justify-center">
            <div className={`h-6 w-12 rounded-md ${barClass} animate-pulse`} />
          </div>
        </td>
      ) : null}
      {showSaveColumn ? (
        <td className={`px-3 py-2 align-middle ${mergedTheme.cellBgClass}`}>
          <div className="flex items-center justify-center">
            <div className={`h-5 w-5 rounded-sm ${barClass} animate-pulse`} />
          </div>
        </td>
      ) : (
        <td className={`px-3 py-2 align-middle ${mergedTheme.cellBgClass}`}>
          <SkeletonBar className="w-16" barClass={barClass} />
        </td>
      )}
      <td className={`px-3 py-2 ${mergedTheme.cellBgClass}`}>
        <div className="flex items-center justify-center">
          <div className={`h-6 w-12 rounded-md ${barClass} animate-pulse`} />
        </div>
      </td>
    </tr>
  ));
}

function Th({ label, column, sortBy, handleSort, getSortArrow }) {
  return (
    <th onClick={() => handleSort(column)} className="px-3 py-2 cursor-pointer">
      <HeaderContent label={label} column={column} {...{ sortBy, getSortArrow }} />
    </th>
  );
}

function HeaderContent({ label, column, getSortArrow }) {
  return (
    <div className="flex items-center gap-1">
      {label}
      <span className="text-[10px] opacity-60">{getSortArrow(column)}</span>
    </div>
  );
}
